import { useStoryblokBridge } from '@storyblok/vue'
import { type Ref, computed, onMounted } from 'vue'
import type { StoryblokStory } from 'storyblok-generate-ts'
import type { ISbError } from 'storyblok-js-client'
import { pickBy } from 'es-toolkit'
import { isStaging, isDevelopment } from '#layers/base/utils/enviroment'
import {
  hasSuppressingQuery,
  pickXDefaultHref,
  stripHreflangTrailingSlash,
} from '#layers/base/utils/hreflang'
import { useRouter } from '#app/composables/router'
import { useCurrentShop, useRpcCall } from '#storefront/composables'
import { useRuntimeConfig } from '#app/nuxt'
import { useState } from '#app/composables/state'
import { createError } from '#app/composables/error'
import locales from '~/config/locales'
import type { NuxtError } from '#app'
import type { StoryData } from '~/cms/storyblok/types/storyblok'
import { isCmsExcluded } from '~/rpcMethods/cms'

const handleCmsError = (error: any): NuxtError => {
  // Tolerate every error shape without a secondary throw: a non-JSON string,
  // a non-object, or undefined previously crashed the destructure / JSON.parse
  // and masked the original CMS error (T5).
  let parsed: Partial<ISbError> = {}
  if (typeof error === 'string') {
    try {
      parsed = JSON.parse(error) as ISbError
    } catch {
      parsed = { message: error }
    }
  } else if (error && typeof error === 'object') {
    parsed = error as ISbError
  }
  throw createError({
    statusCode: parsed.status ?? 500,
    statusMessage: parsed.message ?? 'CMS request failed',
  })
}

export default <T = unknown>(key: string) => {
  // Utility Type to extract Array Element Type
  type ElementType<T> = T extends Array<infer U> ? U : T
  // Return Type based on Generic
  type DataReturnType =
    ElementType<T> extends T
      ? StoryblokStory<T>
      : StoryblokStory<ElementType<T>>[]

  const fetching = useState(`fetching-${key}`, () => false)
  const data = useState<StoryData | null>(`data-${key}`, () => null)
  const error = useState<NuxtError | undefined>(`error-${key}`, () => undefined)
  /** True when RPC returned { excluded: true } (locale in excludedCountries → treat as unpublished, page should 404). */
  const excluded = useState<boolean>(`excluded-${key}`, () => false)

  const _fetch = useRpcCall('getCmsContent')
  const resolveIdsToSlugs = useRpcCall('resolveIdsToSlugs')

  const log = useLog(`useCms:${key}`)
  const config = useRuntimeConfig()
  const currentShop = useCurrentShop()
  const router = useRouter()
  const isInsideStoryblok = '_storyblok' in router.currentRoute.value.query

  const fetch = async (args: {
    slug?: string
    folder?: string
    perPage?: number
    page?: number
    contentType?: string
    resolveRelations?: string
    filterQuery?: Record<string, Record<string, any>>
  }) => {
    const lang = currentShop.value.locale
    const query = router.currentRoute.value.query || {}
    const isInsideStoryblok = '_storyblok' in query
    const storyblokRelease = query?._storyblok_release?.toString()
    let slug = args.slug || ''

    if (slug.startsWith(`${lang}/`)) {
      slug = slug.replace(`${lang}/`, '')
    } else if (slug.startsWith(`/${lang}/`)) {
      slug = slug.replace(`/${lang}/`, '')
    }

    try {
      fetching.value = true
      excluded.value = false
      const options = pickBy(
        {
          starts_with: args.folder,
          per_page: args.perPage,
          language: lang,
          from_release: storyblokRelease,
          page: args.page,
          content_type: args.contentType,
          resolve_relations: args.resolveRelations,
          filter_query: args.filterQuery,
          preview: isInsideStoryblok,
        },
        (v) => v !== undefined,
      )

      const raw = await _fetch({
        slug,
        options,
      })

      if (isCmsExcluded(raw)) {
        data.value = null
        excluded.value = true
      } else {
        data.value = raw
        excluded.value = false
      }
    } catch (e) {
      log.error(`Error fetching CMS Slug: ${slug}`, e)
      handleCmsError(e)
    } finally {
      fetching.value = false
    }
  }

  const metaLinks = computed(() => {
    // Suppress all hreflang when the URL carries any query key other than `page`
    // (filters/sort): such a view is not a canonical hreflang group member
    // (ECOM-2433). Canonical generation is separate and still strips params.
    if (hasSuppressingQuery(router.currentRoute.value.query)) {
      return []
    }

    const story = data.value as any
    const translatedSlugs = story?.translated_slugs as
      | {
          lang: string
          path?: string
          slug?: string
          name?: string
          published?: boolean
        }[]
      | undefined
    const excludedCountries: string[] =
      (story?.content?.excludedCountries as string[] | undefined) ?? []

    if (!translatedSlugs?.length) {
      return []
    }

    const baseUrl = (config.public.baseUrl as string)?.replace(/\/$/, '') || ''
    const currentLocale = currentShop.value.locale
    const currentIso =
      locales.find((l) => l.code === currentLocale)?.iso ?? currentLocale

    // Normalize Storyblok lang (e.g. "de", "de-de", "de-AT") to our locale code for URLs and filtering
    const toLocaleCode = (lang: string) =>
      locales.find(
        (l) => l.code === lang || l.iso?.toLowerCase() === lang?.toLowerCase(),
      )?.code ??
      lang?.split('-')[0] ??
      lang

    // Only include locales that are published and not excluded (avoid 404 hreflang links)
    const publishedSlugs = translatedSlugs.filter((item) => {
      const localeCode = toLocaleCode(item.lang)
      return item.published === true && !excludedCountries.includes(localeCode)
    })

    // Current page path (strip locale prefix from full_slug if present; case-insensitive for e.g. de-DE)
    const pathWithoutLocale =
      (story?.full_slug as string)?.replace(
        /^([a-z]{2}(-[a-z]{2})?\/)?/i,
        '',
      ) ?? ''
    const selfHref = stripHreflangTrailingSlash(
      [baseUrl, currentLocale, pathWithoutLocale].filter(Boolean).join('/'),
    )

    // 1) Self-reference is mandatory for hreflang (Google SEO)
    const linkTags: { rel: 'alternate'; hreflang: string; href: string }[] = [
      { rel: 'alternate', hreflang: currentIso, href: selfHref },
    ]

    // 2) Add only published, non-excluded alternates (exclude current to avoid duplicate)
    for (const item of publishedSlugs) {
      const localeCode = toLocaleCode(item.lang)
      if (localeCode === currentLocale) {
        continue
      }
      const path = item.path ?? item.slug ?? ''
      const href = stripHreflangTrailingSlash(
        [baseUrl, localeCode, path].filter(Boolean).join('/'),
      )
      const iso = locales.find((l) => l.code === localeCode)?.iso ?? localeCode
      linkTags.push({ rel: 'alternate', hreflang: iso, href })
    }

    // 2b) The Storyblok default language is German (de-DE). Its slug lives on the
    // story's base `default_full_slug` field, NOT inside `translated_slugs`, so
    // the loop above never emits it — on non-German pages that silently drops
    // both the de-DE alternate and the x-default derived from it (ECOM-2433, TC4).
    // Emit it explicitly from the default slug unless German is the current page
    // or is excluded for this story.
    const germanLocale = locales.find((l) => l.iso === 'de-DE')
    const defaultFullSlug = story?.default_full_slug as string | undefined
    if (
      germanLocale &&
      currentLocale !== germanLocale.code &&
      defaultFullSlug &&
      !excludedCountries.includes(germanLocale.code) &&
      !linkTags.some((tag) => tag.hreflang === germanLocale.iso)
    ) {
      linkTags.push({
        rel: 'alternate',
        hreflang: germanLocale.iso,
        href: stripHreflangTrailingSlash(
          [baseUrl, germanLocale.code, defaultFullSlug]
            .filter(Boolean)
            .join('/'),
        ),
      })
    }

    // 3) x-default = the de-DE member verbatim (the self-reference carries it on
    // German pages); omit when no published German member exists rather than
    // synthesizing a /de path that may 404 or use the wrong slug (ECOM-2433).
    const xDefaultHref = pickXDefaultHref(linkTags)
    if (xDefaultHref) {
      linkTags.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: xDefaultHref,
      })
    }

    return linkTags
  })

  onMounted(() => {
    if (
      data.value &&
      data.value.id &&
      (isDevelopment || isStaging || isInsideStoryblok)
    ) {
      useStoryblokBridge(data.value.id, async (newStory) => {
        if (data.value?.content) {
          data.value.content = (await resolveIdsToSlugs(
            newStory.content,
          )) as StoryData['content']
        }
      })
    }
  })

  return {
    data: data as unknown as Ref<DataReturnType>,
    fetching,
    error,
    excluded,
    fetchBySlug: (
      slug: string,
      params: {
        contentType?: string
        resolveRelations?: string
      } = {},
    ) => {
      return fetch({ slug, ...params })
    },
    fetchByFolder: (
      folder: string,
      params: {
        perPage?: number
        page?: number
        contentType?: string
        resolveRelations?: string
        filterQuery?: Record<string, Record<string, any>>
        resolveTags?: boolean
      } = {},
    ) => {
      return fetch({ folder, ...params })
    },
    metaLinks,
  }
}
